import React, { useMemo } from "react";
import { Container, Col, Row } from "reactstrap";
import { BreadcrumbItem } from "reactstrap";
import { useQuery } from "@apollo/client";
import _get from "lodash/get";
import { Component as Form } from "@repoflow/form-schema";
import * as Notify from "@repoflow/form-schema/notify";
import ContentTabs from "@repoflow/components/content-tabs";
import {
  Mutation,
  Button,
  useSectionContext,
} from "@repoflow/components/section";
import { CancellableContent } from "@repoflow/components/content-cancel";
import * as widgets from "widgets/register";
import * as QueriesQL from "queries/blog-post";
import * as BlogQL from "queries/blog";
import { authorOptions } from "../../../author-options";

// Two tabs over one value object: the prose and the details. One form each,
// each writing the WHOLE value back (a merge over the snapshot — see
// docs/form-schema.md), and one Save outside both, the shape the page editor
// uses for the same reason.
const CONTENT_FIELDS = {
  "/Public/BlogPost/Update": {
    fields: [{ property: "content", label: "", layout: "wide" }],
  },
};

const detailsFields = (options) => ({
  "/Public/BlogPost/Update": {
    fields: [
      "title",
      "slug",
      { property: "abstract", layout: "wide" },
      { property: "authorId", widget: "select", options },
      "date",
      "tags",
      { property: "image", layout: "wide" },
    ],
  },
});

export const Sidebar = () => null;
export const Indice = () => (
  <>
    <BreadcrumbItem>Edit</BreadcrumbItem>
  </>
);

export const Content = ({
  history,
  match: {
    params: { siteId, postId },
  },
}) => {
  const { base } = useSectionContext();

  return (
    <Mutation
      target={{ $ref: "/Public/BlogPost/Update" }}
      mutation={QueriesQL.edit}
      update={QueriesQL.update()}
      onMutate={({ handler, state: { values } }) => {
        handler({
          variables: {
            siteId,
            postId,
            input: {
              slug: values.slug,
              title: values.title,
              abstract: values.abstract || "",
              image: values.image || { url: "", description: "" },
              tags: values.tags || [],
              authorId: values.authorId || "",
              // The date widget's clear writes null; the API takes "".
              date: values.date || "",
              content: values.content,
            },
          },
        });
      }}
      onCompleted={() => {
        history.replace(base);
      }}
      onError={(error) => {
        Notify.error(error.toString());
      }}
    >
      {MutationHandler}
    </Mutation>
  );
};

const MutationHandler = ({ state, schema, mutate, target }) => {
  const { values, setValues } = state;
  const { attributes: { siteId } } = useSectionContext();

  // Read once, at the top, and memoised — never inside a tab, where a second
  // identity rebuilds the widget tree under whatever is being typed (P139).
  const { data } = useQuery(BlogQL.authors, {
    variables: { siteId },
    fetchPolicy: "cache-first",
  });
  const authors = useMemo(
    () => _get(data, "viewer.account.sites.get.blog.authors") || [],
    [data]
  );
  const options = useMemo(() => authorOptions(authors), [authors]);
  const details = useMemo(() => detailsFields(options), [options]);

  const tabs = useMemo(
    () => [
      {
        label: "Content",
        content: (
          <Form
            schema={schema}
            target={target}
            widgets={widgets}
            values={values}
            onChange={(mod) => setValues(mod)}
            config={CONTENT_FIELDS}
          />
        ),
      },
      {
        label: "Details",
        content: (
          <Form
            schema={schema}
            target={target}
            widgets={widgets}
            values={values}
            onChange={(mod) => setValues(mod)}
            config={details}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schema, target, values, details]
  );

  return (
    <CancellableContent>
      <Container className="mt-4">
        <Row>
          <Col>
            <div className="float-right">
              <Button
                onClick={() => {
                  mutate();
                }}
              >
                Save
              </Button>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <ContentTabs tabs={tabs} />
          </Col>
        </Row>
      </Container>
    </CancellableContent>
  );
};
